/**
 * score's permission manifest (AUTHZ.md §5.2). This module **imports nothing**
 * — the package's `exports` map exposes it as the isolated `score-common/permissions`
 * subpath so importing it never drags in the heavy generated axios client. Both
 * score-api (its own enforcement) and the discrete `permissions` aggregator pull
 * the manifest with zero client bloat.
 *
 * Keys are authored short (no `score:` prefix); the app name is implied by the
 * manifest. The canonical/wire form is `score:<key>` (token, audit log, account
 * admin UI). Roles bundle permissions flatly — no inheritance, no negatives
 * (§5.1): a role that should "include" another copies its permissions in.
 *
 * The object-map shape (keyed by short key) lets ESLint `no-dupe-keys` flag
 * in-file collisions for free; cross-file dupes + role→permission integrity are
 * caught by the aggregator's validator.
 */
export const manifest = {
  app: "score",
  permissions: {
    //  short key            title (UI)              description (UI)                                     group
    list_shows_org: {
      title: "List Shows",
      description: "List all shows for the organization",
      group: "Shows",
    },
    list_shows_mine: {
      title: "List My Shows",
      description: "List only shows you are assigned to",
      group: "Shows",
    },
    edit_score_sheet: {
      title: "Edit Score Sheet",
      description: "Edit scores on an existing score sheet",
      group: "Scoring",
    },
    sign_score_sheet: {
      title: "Sign Score Sheet",
      description: "Apply a judge signature to a score sheet",
      group: "Scoring",
    },
    assign_scribes: {
      title: "Assign Scribes",
      description: "Assign scribes to rings and classes for a show",
      group: "Administration",
    },
    reopen_score_sheet: {
      title: "Reopen Score Sheet",
      description: "Reopen a confirmed or signed score sheet for correction",
      group: "Administration",
    },
    // #1028 — reseed the demo org (org-demo) from a baked demo-dump snapshot.
    // Non-production only (LIVE=false, ENVIRONMENT_NAME != main) and hard-scoped
    // to org-demo by the handler regardless of who holds this permission.
    demo_reseed: {
      title: "Reseed Demo Org",
      description: "Reseed the demo org's show data from a baked demo-dump snapshot",
      group: "Administration",
    },
    // #1658 — PATCH /catalog/:id, the score-catalog table's first write
    // endpoint. Global/no-org data (the catalog is published federation test
    // text, not tenant data — see src/handlers/catalog.js's header), so this
    // gates a correction to the shared def content itself, not anything
    // org-scoped.
    edit_catalog: {
      title: "Edit Catalog",
      description: "Correct a test definition in the global score catalog",
      group: "Administration",
    },
    // #1635 — scoreboard surface (docs/score/scoreboard-design.md §3.3): views,
    // sponsors, themes, and brand assets, plus the remote's mode-switch route.
    manage_scoreboard: {
      title: "Manage Scoreboard",
      description: "Configure and drive the public scoreboards for a show",
      group: "Administration",
    },
    // #1767 — add/remove a movement on a ride's movementOverride. Kept
    // separate from edit_score_sheet: it changes the DENOMINATOR of every
    // judge's sheet on the ride (score-common/utils/movement-override.js),
    // a materially bigger power than editing one mark, so it does not ride
    // on score_scribe's edit_score_sheet grant.
    edit_movement_override: {
      title: "Edit Movement Override",
      description: "Add or remove a movement on a ride, recomputing every judge's sheet",
      group: "Administration",
    },
    // #1342 (judge-identity workstream, card A) — create/update/delete the
    // composite-judge-to-real-judges mapping. Read (list/get/resolve) is
    // open to any org-authenticated user, same as the roster itself
    // (listJudges); only the write side is gated.
    manage_judges: {
      title: "Manage Judges",
      description: "Map a composite judge roster row to its two real judges",
      group: "Administration",
    },
    // #1934 (docs/score/ring-state.md) — explicit ring-state set: Start
    // class (#1935, a scribe action) or Score Admin declaring BREAK/END OF
    // DAY (#1936). Granted to score_scribe too, not just score_admin,
    // because Start class is ordinary scribe work, not an admin action.
    set_ring_state: {
      title: "Set Ring State",
      description: "Explicitly set what a ring is showing (start class, break, end of day)",
      group: "Scoring",
    },
  },
  roles: {
    score_admin: {
      title: "Score Admin",
      description: "Administer scoring for the organization, including scribe assignment",
      // #1635 — score admins get board control by adding the permission
      // straight to this role rather than needing a second role (design §3.3).
      permissions: [
        "list_shows_org",
        "assign_scribes",
        "reopen_score_sheet",
        "demo_reseed",
        "edit_catalog",
        "manage_scoreboard",
        "edit_movement_override",
        "manage_judges",
        "set_ring_state",
      ],
    },
    score_scribe: {
      title: "Scribe",
      description: "Enter and update scores during a show",
      permissions: ["list_shows_org", "edit_score_sheet", "set_ring_state"],
    },
    score_judge: {
      title: "Judge",
      description: "Score and sign sheets for shows you are assigned to",
      // Flat, fully-resolved set — no runtime inheritance (§5.1).
      permissions: ["list_shows_mine", "edit_score_sheet", "sign_score_sheet"],
    },
    score_viewer: {
      title: "Viewer",
      description: "Read-only access to shows",
      permissions: ["list_shows_mine"],
    },
    score_announcer: {
      title: "Announcer",
      description: "Read-only access to the whole show, for ring announcing",
      // list_shows_org (not list_shows_mine, cf. score_viewer above) — an
      // announcer needs the entire show, not just shows they're personally
      // assigned to.
      permissions: ["list_shows_org"],
    },
    // #1635 — scoreboard-admin (design §3.3): drives the remote control
    // (views/sponsors/themes/assets) without needing the full score_admin
    // grant (scribe assignment, sheet reopen, demo reseed).
    score_scoreboard_admin: {
      title: "Scoreboard Admin",
      description: "Configure and drive the public scoreboards for a show",
      permissions: ["manage_scoreboard", "list_shows_org"],
    },
  },
};

export default manifest;
